import { DialogueResult, Scope, HumanRight } from "../types";

const TAVILY_API_KEY = import.meta.env.VITE_TAVILY_API_KEY || "";
const GROQ_API_KEY = import.meta.env.VITE_GROQ_API_KEY || "";

// Groq free tier — OpenAI-compatible, no credit card
// Using openai/gpt-oss-120b which is available on restricted Groq accounts
const GROQ_URL = "https://api.groq.com/openai/v1/chat/completions";
const GROQ_MODEL = "openai/gpt-oss-120b";
const GROQ_FALLBACK = "openai/gpt-oss-20b";

const TAVILY_URL = "https://api.tavily.com/search";

// --- Core helpers ---

async function tavilySearch(query: string, domains?: string[]): Promise<string> {
  const body: Record<string, unknown> = {
    query,
    search_depth: "advanced",
    max_results: 7,
    include_raw_content: false,
    include_answer: false,
  };

  if (domains && domains.length > 0) {
    body.include_domains = domains;
  }

  const response = await fetch(TAVILY_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${TAVILY_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Tavily error ${response.status}: ${await response.text()}`);
  }

  const data = await response.json();

  const results = data.results as Array<{
    title: string;
    url: string;
    content: string;
  }>;

  if (!results || results.length === 0) {
    throw new Error("Tavily returned no results for this query.");
  }

  // 500 chars per result — enough for article numbers, provisions, specific findings
  return results
    .map((r, i) => `[${i + 1}] ${r.title}\nURL: ${r.url}\nContent: ${r.content.slice(0, 500)}`)
    .join("\n\n");
}

async function callGroq(prompt: string, model: string): Promise<string> {
  const response = await fetch(GROQ_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${GROQ_API_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model,
      messages: [{ role: "user", content: prompt }],
      max_tokens: 4000,
      temperature: 0.1,
    }),
  });

  const responseText = await response.text();

  if (response.status === 429) {
    throw new Error("RATE_LIMITED");
  }

  if (!response.ok) {
    throw new Error(`Groq error ${response.status}: ${responseText}`);
  }

  const data = JSON.parse(responseText);
  return data.choices?.[0]?.message?.content ?? "";
}

async function formatWithGroq(prompt: string): Promise<string> {
  try {
    console.log(`🤖 Calling Groq (${GROQ_MODEL})...`);
    const result = await callGroq(prompt, GROQ_MODEL);
    console.log("✅ Groq responded");
    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg === "RATE_LIMITED") {
      console.warn(`⚠️ ${GROQ_MODEL} rate limited, trying ${GROQ_FALLBACK}...`);
      const result = await callGroq(prompt, GROQ_FALLBACK);
      console.log(`✅ Groq fallback (${GROQ_FALLBACK}) responded`);
      return result;
    }
    throw err;
  }
}

function extractJSON(text: string): string {
  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenceMatch) return fenceMatch[1].trim();

  const firstBrace = text.indexOf("{");
  const firstBracket = text.indexOf("[");

  if (firstBrace === -1 && firstBracket === -1) return text;

  let start: number;
  let endChar: string;

  if (firstBrace === -1) { start = firstBracket; endChar = "]"; }
  else if (firstBracket === -1) { start = firstBrace; endChar = "}"; }
  else if (firstBrace < firstBracket) { start = firstBrace; endChar = "}"; }
  else { start = firstBracket; endChar = "]"; }

  const end = text.lastIndexOf(endChar);
  if (end === -1) return text;

  return text.slice(start, end + 1);
}

function parseJSON(text: string): DialogueResult {
  try {
    const cleaned = extractJSON(text);
    const parsed = JSON.parse(cleaned);

    if (Array.isArray(parsed)) {
      return { sources: parsed };
    }

    if (parsed && typeof parsed === "object") {
      for (const key of ["sources", "data", "results", "items"]) {
        if (Array.isArray(parsed[key])) {
          return { sources: parsed[key] };
        }
      }
      if (parsed.title && parsed.uri) {
        return { sources: [parsed] };
      }
    }

    console.warn("⚠️ Unexpected JSON shape from LLM:", JSON.stringify(parsed).slice(0, 200));
    return { sources: [] };
  } catch (e) {
    console.error("❌ JSON parse failed:", e, "Raw text:", text?.slice(0, 200));
    return { sources: [] };
  }
}

// --- Exported functions ---

export async function getScopeAnalysis(
  rightName: string,
  scope: Scope,
  subScope: string
): Promise<DialogueResult> {
  try {
    console.log("🔍 Legal search: fetching from OHCHR and UN sources...");

    const searchQuery = `${rightName} international treaty convention articles provisions ${subScope || ""}`;

    const webContent = await tavilySearch(searchQuery, [
      "ohchr.org",
      "un.org",
      "refworld.org",
      "treaties.un.org",
      "ihl-databases.icrc.org",
    ]);

    console.log("✅ Tavily returned legal content");

    const formatPrompt = `You are a human rights legal analyst. Extract treaty information from these real web search results. Use ONLY information from the sources below — do not invent anything.

SEARCH RESULTS (from OHCHR, UN, Refworld, ICRC):
${webContent}

Task: Extract every distinct legal instrument that protects "${rightName}"${subScope ? ` in the context of ${subScope}` : ""} and return a JSON object with a "sources" array.

Each source MUST have:
- "title": full official name of the treaty/convention WITH year — e.g. "International Covenant on Civil and Political Rights (1966)"
- "uri": exact URL from the search results above
- "reference": SPECIFIC article number AND sub-article — e.g. "Art. 19(2): Everyone shall have the right to freedom of expression" or "Art. 7(a)(i): Equal remuneration for work of equal value"

Rules:
- More sources is better than longer descriptions
- Format references as: "Art. X(Y): [key phrase from that provision]"
- If a snippet mentions multiple articles, create a separate source entry for each distinct article
- Use ONLY URLs from the search results above

Return ONLY valid JSON, no markdown, no explanation:
{"sources": [{"title": "...", "uri": "...", "reference": "Art. X: ..."}]}`;

    const formatted = await formatWithGroq(formatPrompt);
    const parsed = parseJSON(formatted);
    console.log("✅ Legal sources parsed:", parsed?.sources?.length ?? 0);
    return parsed;
  } catch (error) {
    console.error("❌ Legal search failed:", error instanceof Error ? error.message : String(error));
    return {
      sources: [{
        title: "Legal framework information temporarily unavailable",
        uri: "https://www.ohchr.org/en/instruments-listings",
        reference: "Visit the UN Office of the High Commissioner for Human Rights for official treaty texts.",
      }],
    };
  }
}

export async function getStatusAnalysis(
  rightName: string,
  scope: Scope,
  subScope: string
): Promise<DialogueResult> {
  const region = subScope || "globally";

  try {
    console.log(`🔍 Status search: fetching HRW/Amnesty reports for ${rightName} in ${region}...`);

    const searchQuery = `${rightName} violations ${region} 2024 2025 human rights report findings`;

    const webContent = await tavilySearch(searchQuery, [
      "hrw.org",
      "amnesty.org",
      "ohchr.org",
      "un.org",
      "icj.org",
    ]);

    console.log("✅ Tavily returned status content");

    const formatPrompt = `You are a human rights researcher. Extract specific findings from these real monitoring reports. Use ONLY what is in the sources below — do not invent quotes or findings.

SEARCH RESULTS (from HRW, Amnesty International, OHCHR, UN, ICJ):
${webContent}

Task: Extract specific documented findings about the status of "${rightName}" in "${region}" and return a JSON object with a "sources" array.

Each source MUST have:
- "title": exact report title with organization and year — e.g. "World Report 2024: Events of 2023 (Human Rights Watch)"
- "uri": exact URL from the search results above
- "reference": specific finding with context — include section name, country chapter, or finding number if visible — e.g. "Chapter: Mexico — 'Security forces committed at least 43 documented cases of enforced disappearance in 2023'"

Rules:
- More specific findings across more sources is better than one long quote
- If a source covers multiple countries/findings relevant to "${rightName}", create a separate entry per finding
- Keep each reference to 1–2 sentences — specific and pinpointed
- Use ONLY URLs from the search results above

Return ONLY valid JSON, no markdown, no explanation:
{"sources": [{"title": "...", "uri": "...", "reference": "..."}]}`;

    const formatted = await formatWithGroq(formatPrompt);
    const parsed = parseJSON(formatted);
    console.log("✅ Status reports parsed:", parsed?.sources?.length ?? 0);
    return parsed;
  } catch (error) {
    console.error("❌ Status search failed:", error instanceof Error ? error.message : String(error));
    return {
      sources: [{
        title: "Current status information temporarily unavailable",
        uri: "https://www.hrw.org/world-report/2024",
        reference: `Check Human Rights Watch and Amnesty International for recent reports on ${rightName}.`,
      }],
    };
  }
}

export async function getNexusAnalysis(
  fromRight: string,
  toRight: string,
  scope: Scope,
  subScope: string
): Promise<DialogueResult> {
  try {
    console.log(`🔍 Nexus search: fetching scholarship on ${fromRight} ↔ ${toRight}...`);

    const searchQuery = `${fromRight} ${toRight} interdependence indivisibility human rights law principle`;

    const webContent = await tavilySearch(searchQuery, [
      "ohchr.org",
      "un.org",
      "jstor.org",
      "academic.oup.com",
      "cambridge.org",
      "hrw.org",
      "amnesty.org",
      "refworld.org",
    ]);

    console.log("✅ Tavily returned nexus content");

    const formatPrompt = `You are a human rights legal scholar. Extract specific doctrinal connections from these real sources. Use ONLY what is in the sources below.

SEARCH RESULTS:
${webContent}

Task: Extract specific legal principles, doctrines, or findings that establish how "${fromRight}" and "${toRight}" are interconnected, and return a JSON object with a "sources" array.

Each source MUST have:
- "title": exact document/article title with author/organization and year if available
- "uri": exact URL from the search results above
- "reference": SPECIFIC principle, doctrine, or legal connection — cite exact legal instrument, case name, resolution number, or scholarly argument — e.g. "Vienna Declaration (1993) §5: Human rights are indivisible and interdependent" or "General Comment No. 31 (CCPR): The Covenant establishes..."

Rules:
- Prioritize specific legal instruments, case law, UN resolutions, and General Comments over general statements
- If a source mentions multiple connecting principles, create a separate entry for each
- Use ONLY URLs from the search results above

Return ONLY valid JSON, no markdown, no explanation:
{"sources": [{"title": "...", "uri": "...", "reference": "..."}]}`;

    const formatted = await formatWithGroq(formatPrompt);
    const parsed = parseJSON(formatted);
    console.log("✅ Nexus perspectives parsed:", parsed?.sources?.length ?? 0);
    return parsed;
  } catch (error) {
    console.error("❌ Nexus search failed:", error instanceof Error ? error.message : String(error));
    return {
      sources: [{
        title: "Scholarly perspectives temporarily unavailable",
        uri: `https://scholar.google.com/scholar?q=${encodeURIComponent(`"${fromRight}" "${toRight}" human rights`)}`,
        reference: `Explore how ${fromRight} and ${toRight} are interconnected via the search link above.`,
      }],
    };
  }
}

export async function getSemanticRights(
  term: string,
  rights: HumanRight[]
): Promise<string[]> {
  const prompt = `Given the search term: "${term}", identify which of the following human rights are most relevant.

Rights list:
${JSON.stringify(rights.map((r) => ({ id: r.id, name: r.name, summary: r.summary })))}

Return ONLY a JSON object with an "ids" array of matching ID strings. Example: {"ids": ["1", "5"]}
Match by conceptual relevance, not just keyword. Return 1–5 most relevant IDs.`;

  try {
    console.log("🔍 Semantic matching for:", term);
    const response = await formatWithGroq(prompt);

    let parsed: unknown;
    try {
      parsed = JSON.parse(extractJSON(response));
    } catch {
      console.warn("⚠️ Semantic response not valid JSON:", response?.slice(0, 100));
      return [];
    }

    if (!parsed || typeof parsed !== "object") return [];
    if (Array.isArray(parsed)) return parsed as string[];

    const obj = parsed as Record<string, unknown>;
    if (Array.isArray(obj.ids)) return obj.ids as string[];

    for (const key of Object.keys(obj)) {
      if (Array.isArray(obj[key])) return obj[key] as string[];
    }

    return [];
  } catch (error) {
    console.error("❌ Semantic search failed:", error instanceof Error ? error.message : String(error));
    return [];
  }
}
